import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { DriverAvatar } from '@/components/ui/driver-avatar'
import OperationalSummarySkeleton from '../Skeletons/OperationalSummarySkeleton'
import { InlineErrorDisplay } from '@/presentation/components/ui/ErrorDisplay'

interface Summary {
  totalDrivers: number
  onlineDrivers: number
  offlineDrivers: number
  averageRating: number
  onlinePercentage: number
  totalRides: number
  canceledRides: number
  topDriversByRides?: DriverRideStats[]
  lowDriversByRides?: DriverRideStats[]
}

interface SolicitationMetrics {
  total: number
  accepted: number
  refused: number
  canceled: number
  pending: number
}

interface DriverForPeriod {
  id: string | number
  name: string
  photo?: string
  ridesCompleted: number
  commission: number
  rating: number
}

interface DriverRideStats {
  driverId: string
  totalRides: number
  completedRides?: number
  canceledRides?: number
  comissionEarnings?: number
}

interface AnalyticsTabProps {
  summary: Summary
  solicitations?: SolicitationMetrics | null
  driversForPeriod: DriverForPeriod[]
  periodLabel: string
  isLoadingSolicitations?: boolean
  solicitationsError?: Error | null
}

function formatCurrency(value?: number) {
  const v = value ?? 0
  return v.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  })
}

function formatRidesText(count: number): string {
  if (count === 0) {
    return 'nenhuma corrida'
  }
  if (count === 1) {
    return 'corrida'
  }
  return 'corridas'
}

type MergedDriver = {
  driverId: string
  name?: string
  photo?: string
  rating?: number
  ridesCompleted?: number
  comissionEarnings?: number
} | null

export default function AnalyticsTab({
  summary,
  solicitations,
  driversForPeriod,
  periodLabel,
  isLoadingSolicitations = false,
  solicitationsError = null,
}: AnalyticsTabProps) {
  const rawTop = summary.topDriversByRides ?? []

  const mergedTop: MergedDriver[] = rawTop.map((stat) => {
    const driverDetails = driversForPeriod.find(
      (d) => String(d.id) === String(stat.driverId)
    )

    return {
      driverId: stat.driverId,
      name: driverDetails?.name,
      photo: driverDetails?.photo,
      rating: driverDetails?.rating,
      ridesCompleted: stat.totalRides ?? driverDetails?.ridesCompleted ?? 0,
      comissionEarnings:
        stat.comissionEarnings ?? driverDetails?.commission ?? 0,
    }
  })

  const topWithSlots: MergedDriver[] = mergedTop
    .slice(0, 3)
    .concat(Array(Math.max(0, 3 - mergedTop.length)).fill(null))

  const rawLow = summary.lowDriversByRides ?? []

  const mergedLow: MergedDriver[] = rawLow.map((stat) => {
    const driverDetails = driversForPeriod.find(
      (d) => String(d.id) === String(stat.driverId)
    )

    return {
      driverId: stat.driverId,
      name: driverDetails?.name,
      photo: driverDetails?.photo,
      rating: driverDetails?.rating,
      ridesCompleted: stat.totalRides ?? driverDetails?.ridesCompleted ?? 0,
      comissionEarnings:
        stat.comissionEarnings ?? driverDetails?.commission ?? 0,
    }
  })
  const lowWithSlots: MergedDriver[] = mergedLow
    .slice(0, 3)
    .concat(Array(Math.max(0, 3 - mergedLow.length)).fill(null))

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Top Performers</CardTitle>
          <CardDescription>
            Motoristas com melhor desempenho nas corridas ({periodLabel})
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {topWithSlots.map((d, index) => (
              <div
                key={d?.driverId ?? `empty-top-${index}`}
                className="flex items-center gap-3"
              >
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-background text-text-subtitle text-sm font-bold">
                  {index + 1}
                </div>
                {d ? (
                  <>
                    <DriverAvatar photo={d.photo} name={d.name} size="sm" />

                    <div className="flex-1">
                      <p className="font-medium">{d.name ?? 'Desconhecido'}</p>
                      <p className="text-sm text-muted-foreground">
                        {d.ridesCompleted === 0 ? '' : `${d.ridesCompleted} `}
                        {formatRidesText(d.ridesCompleted ?? 0)}
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="font-medium text-green-600">
                        {formatCurrency(d.comissionEarnings)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {d.rating ?? 0} ⭐
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 text-muted-foreground italic">
                    Sem motorista
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Bad Performers</CardTitle>
          <CardDescription>
            Motoristas com pior desempenho nas corridas ({periodLabel})
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {lowWithSlots.map((d, index) => (
              <div
                key={d?.driverId ?? `empty-low-${index}`}
                className="flex items-center gap-3"
              >
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-background text-text-subtitle text-sm font-bold">
                  {index + 1}
                </div>

                {d ? (
                  <>
                    <DriverAvatar photo={d.photo} name={d.name} size="sm" />

                    <div className="flex-1">
                      <p className="font-medium">{d.name ?? 'Desconhecido'}</p>
                      <p className="text-sm text-muted-foreground">
                        {d.ridesCompleted === 0 ? '' : `${d.ridesCompleted} `}
                        {formatRidesText(d.ridesCompleted ?? 0)}
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="font-medium text-green-600">
                        {formatCurrency(d.comissionEarnings)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {d.rating ?? 0} ⭐
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 text-muted-foreground italic">
                    Sem motorista
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {isLoadingSolicitations ? (
        <OperationalSummarySkeleton />
      ) : solicitationsError ? (
        <Card>
          <CardHeader>
            <CardTitle>Resumo Operacional</CardTitle>
            <CardDescription>
              Estatísticas da operação {periodLabel}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <InlineErrorDisplay message={solicitationsError.message} />
          </CardContent>
        </Card>
      ) : solicitations ? (
        <Card>
          <CardHeader>
            <CardTitle>Resumo Operacional</CardTitle>
            <CardDescription>
              Estatísticas da operação {periodLabel}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span>Total de Solicitações Recebidas</span>
                <span className="font-bold">{solicitations.total}</span>
              </div>

              <div className="flex justify-between items-center">
                <span>Total de Solicitações Aceitas</span>
                <span className="font-bold">{solicitations.accepted}</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Total de Solicitações Recusadas pelo Motorista</span>
                <span className="font-bold">{solicitations.refused}</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Total de Solicitações Canceladas</span>
                <span className="font-bold">{solicitations.canceled}</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Total de Corridas Finalizadas</span>
                <span className="font-bold">{summary.totalRides}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  )
}
